/**
 * Wire schemas for the patient-photo upload/read surface (`BACKLOG.md` C18) —
 * `PUT`/`GET /api/patients/:patientId/photo`, implemented by
 * `server/src/documents/patientPhotoRoutes.ts` against `server/src/documents/storage.ts`'s
 * `DocumentStorage`. Shared so server and web cannot validate differently, same reasoning
 * `clinicalRequests.ts`'s own header states for the rest of the patient surface.
 *
 * `PATIENT_PHOTO_MIME_TYPES` is a two-member subset of `documentVerification.ts`'s
 * `DOCUMENT_MIME_TYPES` — a photo is never a PDF, so the third member of that set has no business
 * being accepted here even though `verifyMagicBytes` itself would happily check it.
 *
 * The upload body is JSON with base64-encoded bytes, not `multipart/form-data`: nothing in this
 * codebase parses a multipart body anywhere (checked before choosing this shape), every other route
 * in this project validates a `c.req.json()` body against a Zod `strictObject`
 * (`web/src/api/client.ts`'s `apiRequest` only ever sends JSON), and introducing a second body-
 * parsing mechanism for the one route that needs to carry bytes would be a bigger, riskier change
 * than base64-encoding a few hundred KB of image data client-side. `MAX_PATIENT_PHOTO_BYTES` bounds
 * the request regardless of the encoding overhead that choice costs.
 */

import { z } from 'zod';

/** A photo is always a still image — never a PDF, unlike a general document upload. */
export const PATIENT_PHOTO_MIME_TYPES = ['image/jpeg', 'image/png'] as const;
export type PatientPhotoMimeType = (typeof PATIENT_PHOTO_MIME_TYPES)[number];

const MAX_PATIENT_PHOTO_FILENAME_LENGTH = 255;

/**
 * 8 MiB of raw image bytes — comfortably above a real phone-camera JPEG or a webcam capture (typically
 * a few hundred KB to low single-digit MB even before any client-side compression), while still
 * bounding the request body a caller can send. Not a claim about what a "reasonable" photo looks
 * like, just a generous ceiling.
 */
export const MAX_PATIENT_PHOTO_BYTES = 8 * 1024 * 1024;

/** Base64 expands raw bytes by 4/3 (padded to a multiple of 4 characters) — the wire-format string
 * this schema actually validates is capped against that expansion, not the raw byte count. */
const MAX_PATIENT_PHOTO_BASE64_LENGTH = Math.ceil((MAX_PATIENT_PHOTO_BYTES * 4) / 3) + 4;

/** `PUT /api/patients/:patientId/photo` — `patient:write`. Replaces the patient's current photo,
 * if any; there is no history of prior photos to preserve (`BACKLOG.md` C18's own decision). */
export const uploadPatientPhotoRequestSchema = z.strictObject({
  /** Carried through to `documents/storage.ts`'s upload pipeline as object metadata only — never
   * used to build the storage key. See `UploadDocumentFile.filename`'s own comment. */
  filename: z.string().trim().min(1).max(MAX_PATIENT_PHOTO_FILENAME_LENGTH),
  /** Verified against the real bytes' magic-byte signature server-side
   * (`@emr/shared`'s `verifyMagicBytes`) before anything is stored — never trusted on its own. */
  declaredMimeType: z.enum(PATIENT_PHOTO_MIME_TYPES),
  contentBase64: z.base64().min(1).max(MAX_PATIENT_PHOTO_BASE64_LENGTH),
});
export type UploadPatientPhotoRequest = z.infer<typeof uploadPatientPhotoRequestSchema>;

/** `GET`/`PUT /api/patients/:patientId/photo`'s success response — a presigned, time-limited URL
 * (`DocumentStorage.getDocumentUrl`), never a raw bucket URL. */
export const patientPhotoUrlResponseSchema = z.strictObject({
  url: z.string(),
});
export type PatientPhotoUrlResponse = z.infer<typeof patientPhotoUrlResponseSchema>;
